import { Box, Button, Card, CardContent, Stack, Typography, Chip } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import React, { useState } from 'react';

import MuiPageWrapper from '@/@mui/MuiPageWrapper';
import {
  ApiResponse_GetAll_PropertyManager,
  useGetAllPropertyManagers,
} from '@/api/endpoints/propertyManager/useGetAllPropertyManagers';
import { PropertyManagementWithManagerWithLimitedUserView } from '@/api/models/propertyManagement';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { useAuth } from '@/context/AuthProvider';
import { usePropertyManager } from '@/context/PropertyManagerProvider';
import { formatPhoneNumber } from '@/utils/phone';

import RowActionsMenu from '@/components/common/RowActionsMenu';
import PropertyManagerDeleteModal from '@/components/propertyManager/PropertyManagerDeleteModal';
import PropertyManagerUpdateModal from '@/components/propertyManager/PropertyManagerUpdateModal';

import InviteHouseholdMemberModal from './InviteHouseholdMemberModal';

const InviteHouseholdMemberPage: React.FC = () => {
  const { user } = useAuth();
  const { selectedPropertyLocation } = usePropertyManager();

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isPropertyManagerUpdateModalOpen, setIsPropertyManagerUpdateModalOpen] = useState(false);
  const [isPropertyManagerDeleteModalOpen, setIsPropertyManagerDeleteModalOpen] = useState(false);

  const [propertyManagerToUpdate, setPropertyManagerToUpdate] =
    useState<PropertyManagementWithManagerWithLimitedUserView | null>(null);
  const [propertyManagerToDelete, setPropertyManagerToDelete] =
    useState<PropertyManagementWithManagerWithLimitedUserView | null>(null);

  const getAllPropertyManagersQuery = useGetAllPropertyManagers({
    location_id: selectedPropertyLocation?.id,
  });

  const propertyManagerColumns: GridColDef<ApiResponse_GetAll_PropertyManager[0]>[] = [
    {
      field: 'full_name',
      headerName: 'Name',
      flex: 1,
      renderCell: (params: GridRenderCellParams<ApiResponse_GetAll_PropertyManager[0]>) => (
        <Stack direction="row" spacing={1} alignItems="center">
          {params.row.manager.limitedUser.full_name}
          {params.row.manager.is_emergency_contact && (
            <Chip label="Emergency Contact" color="error" size="small" variant="outlined" />
          )}
        </Stack>
      ),
    },
    {
      field: 'manager.limitedUser.email',
      headerName: 'Email',
      flex: 1,
      valueGetter: (value: undefined, row: ApiResponse_GetAll_PropertyManager[0]) =>
        row.manager.limitedUser.email,
    },
    {
      field: 'manager.limitedUser.phone',
      headerName: 'Phone',
      flex: 1,
      valueGetter: (value: undefined, row: ApiResponse_GetAll_PropertyManager[0]) =>
        row.manager.limitedUser.phone,
      valueFormatter: (value?: string) => (value ? formatPhoneNumber(value) : '–'),
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 120,
      renderCell: (params: GridRenderCellParams<ApiResponse_GetAll_PropertyManager[0]>) => (
        <Chip
          label={params.value}
          size="small"
          variant="outlined"
          color={params.value === 'ADMIN' ? 'primary' : 'secondary'}
        />
      ),
    },
    {
      field: 'actions',
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      headerName: '',
      width: 50,
      renderCell: (params: GridRenderCellParams<ApiResponse_GetAll_PropertyManager[0]>) => {
        const isCurrentUser = user?.id === params.row.manager.limitedUser.id;
        const isCurrentUserAdmin = params.row.role === 'ADMIN';
        const shouldHideActions = isCurrentUser && isCurrentUserAdmin;

        const items = [];

        if (!shouldHideActions) {
          items.push({
            label: 'Edit',
            onClick: () => {
              setPropertyManagerToUpdate(params.row);
              setIsPropertyManagerUpdateModalOpen(true);
            },
          });

          items.push({
            label: 'Remove',
            onClick: () => {
              setPropertyManagerToDelete(params.row);
              setIsPropertyManagerDeleteModalOpen(true);
            },
          });
        }

        return items.length > 0 ? <RowActionsMenu items={items} /> : null;
      },
    },
  ];

  return (
    <MuiPageWrapper>
      <Box sx={{ width: '100%', px: 3, py: 4 }}>
        <Card sx={{ width: '100%' }}>
          <CardContent sx={{ p: 3 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 3 }}
            >
              <Box>
                <Typography variant="h6" gutterBottom>
                  User Management
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage users for {selectedPropertyLocation?.address_text || 'this property'}
                </Typography>
              </Box>

              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  startIcon={<PlusIcon />}
                  onClick={() => setIsInviteModalOpen(true)}
                  sx={{ minWidth: '140px' }}
                >
                  Invite User
                </Button>
              </Stack>
            </Stack>

            <DataGrid
              rows={getAllPropertyManagersQuery.data ?? []}
              columns={propertyManagerColumns}
              density="comfortable"
              disableColumnFilter
              disableColumnMenu
              disableEval
              loading={getAllPropertyManagersQuery.isLoading}
              disableRowSelectionOnClick
              sx={{
                minHeight: 400,
                width: '100%',
                '& .MuiDataGrid-root': {
                  width: '100%',
                },
              }}
            />
          </CardContent>
        </Card>
      </Box>

      {isInviteModalOpen && selectedPropertyLocation && (
        <InviteHouseholdMemberModal
          onClose={() => setIsInviteModalOpen(false)}
          propertyLocationId={selectedPropertyLocation.id}
          onHouseholdMemberCreated={() => {
            getAllPropertyManagersQuery.refetch();
          }}
        />
      )}

      {isPropertyManagerUpdateModalOpen && propertyManagerToUpdate && (
        <PropertyManagerUpdateModal
          propertyManager={propertyManagerToUpdate}
          onClose={() => {
            setPropertyManagerToUpdate(null);
            setIsPropertyManagerUpdateModalOpen(false);
          }}
        />
      )}

      {isPropertyManagerDeleteModalOpen && propertyManagerToDelete && (
        <PropertyManagerDeleteModal
          propertyManager={propertyManagerToDelete}
          onClose={() => {
            setPropertyManagerToDelete(null);
            setIsPropertyManagerDeleteModalOpen(false);
          }}
          onPropertyManagerDeleted={() => {
            getAllPropertyManagersQuery.refetch();
          }}
        />
      )}
    </MuiPageWrapper>
  );
};

export default InviteHouseholdMemberPage;
